import dayjs from 'dayjs';

import { addNotify } from 'features/notify';
import { getErrorMessage } from 'shared/utils/getErrorMessage';
import { callback } from 'chart.js/helpers';
import { reformatNumber } from 'shared/helpers';

const actionTypes = {
  ACTION_PROCESSING: 'reports/ACTION_PROCESSING',
  GET_STATISTIC_PROVIDER: 'reports/GET_STATISTIC_PROVIDER',
  GET_PAYMENT_HISTORY: 'reports/GET_PAYMENT_HISTORY',
  GET_PAYMENT_HISTORY_TOTAL: 'reports/GET_PAYMENT_HISTORY_TOTAL',
  GET_USER_STATISTICS: 'reports/GET_USER_STATISTICS',
  GET_SLOTS_HISTORY: 'reports/GET_SLOTS_HISTORY',
  GET_BETS_HISTORY: 'reports/GET_BETS_HISTORY',
  GET_PLAYER_DEPOSITS: 'reports/GET_PLAYER_DEPOSITS',
  GET_CBU: 'reports/GET_CBU',
  GET_REQUEST_HISTORY: 'reports/GET_REQUEST_HISTORY',
  GET_CASHBACK_HISTORY: 'reports/GET_CASHBACK_HISTORY',
  ACTIVATE_CASHBACK_FOR_ONE_USER: 'reports/ACTIVATE_CASHBACK_FOR_ONE_USER',
  ACTIVATE_CASHBACK_FOR_STRUCTURE: 'reports/ACTIVATE_CASHBACK_FOR_STRUCTURE',
};

const WEEK_MILLISECONDS = 7 * 24 * 60 * 60 * 1000;

const getDiffByPercent = (current, prev) => {
  const result = (reformatNumber(current) / reformatNumber(prev) - 1) * 100;

  if (prev === '0') return 0;

  return Math.round(result * 100) / 100 || 0;
};

function getStatisticProvider(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;

    const requestedBody = {
      date_from: dayjs(form.date.beginDate)?.format('YYYY-MM-DD'),
      date_to: dayjs(form.date.endDate)?.format('YYYY-MM-DD'),
      is_direct_only: form?.structure?.name === 'directOnly' ? true : false,
    };

    if (selectedAgentId) {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

      const response = await api.reports.getStatisticProvider({ id: selectedAgentId, requestedBody });

      if (form?.withDiff && response.success) {
        const requestedBodyDiff = {
          date_from: dayjs(form?.diffDate?.compareFrom)?.format('YYYY-MM-DD'),
          date_to: dayjs(form?.diffDate?.compareTo)?.format('YYYY-MM-DD'),
          is_direct_only: form?.structure?.name === 'directOnly' ? true : false,
        };

        const diffResponse = await api.reports.getStatisticProvider({
          id: selectedAgentId,
          requestedBody: requestedBodyDiff,
        });

        if (diffResponse?.success && diffResponse?.data?.detail && response?.data?.detail) {
          const diffDetail = diffResponse?.data?.detail;
          const currentDetail = response?.data?.detail;
          const diff = {
            betCount: diffDetail?.betCount,
            betCountPercent: getDiffByPercent(currentDetail?.betCount, diffDetail?.betCount),
            betAmount: diffDetail?.betAmount,
            betAmountPercent: getDiffByPercent(currentDetail?.betAmount, diffDetail?.betAmount),
            winAmount: diffDetail?.winAmount,
            winAmountPercent: getDiffByPercent(currentDetail?.winAmount, diffDetail?.winAmount),
            incomeAmount: diffDetail?.incomeAmount,
            incomeAmountPercent: getDiffByPercent(currentDetail?.incomeAmount, diffDetail?.incomeAmount),
            commissionBalance: diffDetail?.commissionBalance,
            commissionBalancePercent: getDiffByPercent(currentDetail?.commissionBalance, diffDetail?.commissionBalance),
            margin: diffDetail?.margin,
            marginPercent: (
              +currentDetail?.margin?.replace(',', '.') - +diffDetail?.margin?.replace(',', '.')
            )?.toFixed(2),
          };
          const providersDiff = {};
          Object.keys(response.data.providers)?.forEach(providerKey => {
            const currentProvider = response.data.providers[providerKey];
            const diffProvider = diffResponse?.data?.providers[providerKey];

            if (currentProvider && diffProvider) {
              providersDiff[providerKey] = {
                betCount: diffProvider?.betCount,
                betCountPercent: getDiffByPercent(currentProvider?.betCount, diffProvider?.betCount),
                incomeAmount: diffProvider?.incomeAmount,
                incomeAmountPercent: getDiffByPercent(currentProvider?.incomeAmount, diffProvider?.incomeAmount),
                provider: diffProvider?.provider,
              };
            }
          });
          dispatch({
            type: actionTypes.GET_STATISTIC_PROVIDER,
            payload: {
              ...response.data,
              diff,
              providersDiff,
            },
          });
        } else {
          const { locale } = getState().locale;
          const errorMessage = getErrorMessage(locale, diffResponse);
          dispatch(addNotify(errorMessage, 'error'));
        }
      } else if (response.success) {
        dispatch({
          type: actionTypes.GET_STATISTIC_PROVIDER,
          payload: response.data,
        });
      } else {
        const { locale } = getState().locale;
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else return;
  };
}

export default function validatePaymentHistoryForm(form) {
  const dateFrom = dayjs(form.beginDate);
  const dateTo = dayjs(form.endDate);

  if (!dateFrom.isValid() || !dateTo.isValid()) {
    return false;
  }

  if (dateTo.isBefore(dateFrom)) {
    return false;
  }

  const duration = dateTo.diff(dateFrom);

  return duration <= WEEK_MILLISECONDS;
}

function getPaymentHistory(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const {
      locale: { locale },
      tree: { selectedAgentId },
    } = getState();

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const validationResult = validatePaymentHistoryForm(form);

    if (!validationResult) {
      dispatch(addNotify(locale.reports.paymentHistoryError, 'error'));
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
      return null;
    }

    const requestedBody = {
      date_from: dayjs(form.beginDate).format('YYYY-MM-DDTHH:mm:ss'),
      date_to: dayjs(form.endDate).format('YYYY-MM-DDTHH:mm:ss'),
      username: form.username,
      role: form.role,
      is_direct_structure: form.isDirectOnly,
      is_higher_transaction_only: form.isHigher,
      is_withdrawal_transfers: form.isWithdrawal,
      is_deposit_transfers: form.isDeposit,
      page: form.page,
      count: form.count,
      is_bonus_deposits: form.isBonus,
      is_referred: form.isReferral || undefined,
      transfers_only: true,
    };

    if (selectedAgentId) {
      const response = await api.reports.getPaymentHistory({ id: selectedAgentId, requestedBody });

      if (response.success) {
        dispatch({
          type: actionTypes.GET_PAYMENT_HISTORY,
          payload: response.data,
        });
      } else {
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
      return null;
    }
  };
}

function getPaymentHistoryTotals(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const {
      locale: { locale },
      tree: { selectedAgentId },
    } = getState();

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const requestedBody = {
      date_from: dayjs(form.beginDate).format('YYYY-MM-DDTHH:mm:ss'),
      date_to: dayjs(form.endDate).format('YYYY-MM-DDTHH:mm:ss'),
      username: form.username,
      role: form.role,
      is_direct_structure: form.isDirectOnly,
      is_higher_transaction_only: form.isHigher,
      is_withdrawal_transfers: form.isWithdrawal,
      is_deposit_transfers: form.isDeposit,
      page: form.page,
      count: form.count,
      is_bonus_deposits: form.isBonus,
      is_referred: form.isReferral || undefined,
      transfers_only: false,
    };

    if (selectedAgentId) {
      const response = await api.reports.getPaymentHistoryTotals({ id: selectedAgentId, requestedBody });

      if (response.success) {
        dispatch({
          type: actionTypes.GET_PAYMENT_HISTORY_TOTAL,
          payload: response.data,
        });
      } else {
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
      return;
    }
  };
}

function getStatisticPlayer(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;

    const requestedBody = {
      date_from: dayjs(form.beginDate).format('YYYY-MM-DD'),
      date_to: dayjs(form.endDate).format('YYYY-MM-DD'),
      username: form.username,
      is_direct_structure: form.isDirectOnly,
      page: form.page,
      count: form.count,
    };

    if (selectedAgentId) {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

      const response = await api.reports.getStatisticPlayer({ id: selectedAgentId, requestedBody });

      if (response.success) {
        dispatch({
          type: actionTypes.GET_USER_STATISTICS,
          payload: response.data,
        });
      } else {
        const { locale } = getState().locale;
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else {
      return;
    }
  };
}

function getSlotsHistory(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;

    const requestedBody = {
      date_from: dayjs(form.beginDate).format('YYYY-MM-DDTHH:mm:ss'),
      date_to: dayjs(form.endDate).format('YYYY-MM-DDTHH:mm:ss'),
      username: form.username,
      page: form.page,
      count: form.count,
      game_type: form.gameType,
      provider: form.provider,
      status: form.status,
    };
    if (selectedAgentId) {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

      const response = await api.reports.getSlotsHistory({ id: selectedAgentId, requestedBody });

      if (response.success) {
        dispatch({
          type: actionTypes.GET_SLOTS_HISTORY,
          payload: response.data,
        });
      } else {
        const { locale } = getState().locale;
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else {
      return;
    }
  };
}

function getBetsHistory(requestedBody) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;

    if (selectedAgentId) {
      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

      const response = await api.reports.getBetsHistory({ id: selectedAgentId, requestedBody });

      if (response.success) {
        dispatch({
          type: actionTypes.GET_BETS_HISTORY,
          payload: response.data,
        });
      } else {
        const { locale } = getState().locale;
        const errorMessage = getErrorMessage(locale, response);
        dispatch(addNotify(errorMessage, 'error'));
      }

      dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
    } else return;
  };
}

function getCBU() {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.getCBU();

    if (response.success) {
      dispatch({
        type: actionTypes.GET_CBU,
        payload: response.data,
      });
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function setCBU(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    const locale = getState().locale.locale;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.setCBU(requestedBody);

    if (response.success) {
      dispatch(addNotify(locale.defaultSuccess, 'success'));
      if (callback) callback();
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function getPlayerDeposits(requestedBody) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.getPlayerDeposits(requestedBody);

    if (response.success) {
      dispatch({
        type: actionTypes.GET_PLAYER_DEPOSITS,
        payload: response.data,
      });
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function changePlayerDeposit(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.changePlayerDeposit(requestedBody);

    if (response.success) {
      if (callback) callback();
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function setDepositBonusPercent(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.setDepositBonusPercent(requestedBody);

    if (response.success) {
      if (callback) callback();
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function createBank(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.createBank(requestedBody);

    if (response.success) {
      if (callback) callback();
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function updateBank(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.updateBank(requestedBody);

    if (response.success) {
      if (callback) callback();
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function deleteBank(requestedBody, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.cbuPayment.deleteBank(requestedBody);

    if (response.success) {
      if (callback) callback();
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function getRequestHistory(requestedBody) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const { selectedAgentId } = getState().tree;

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });
    const response = await api.reports.getRequestHistory({
      requestedBody: { ...requestedBody, role: 0, status: 1 },
      id: selectedAgentId,
    });

    if (response.success) {
      dispatch({ type: actionTypes.GET_REQUEST_HISTORY, payload: response.data });
    } else {
      const { locale } = getState().locale;
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function getCashbackHistory(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const {
      locale: { locale },
    } = getState();

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const requestedBody = {
      date_from: dayjs(form.beginDate).format('YYYY-MM-DD'),
      date_to: dayjs(form.endDate).format('YYYY-MM-DD'),
      username: form.username || null,
      user_id: form.id || null,
      count: form.count,
      page: form.page,
    };

    const response = await api.reports.getCashbackHistory(requestedBody);

    if (response.success) {
      dispatch({
        type: actionTypes.GET_CASHBACK_HISTORY,
        payload: response.data,
      });
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function activateCashbackForOneUser(form, callback) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const {
      locale: { locale },
    } = getState();

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const requestedBody = {
      activate: form.activate,
      user_id: form.id || null,
    };

    const response = await api.reports.activateCashbackForOneUser(requestedBody);

    if (response.success) {
      dispatch({
        type: actionTypes.ACTIVATE_CASHBACK_FOR_ONE_USER,
        payload: response.data,
      });
      dispatch(addNotify(locale.defaultSuccess, 'success'));
      if (callback && typeof callback === 'function') callback();
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

function activateCashbackForStructure(form) {
  return async (dispatch, getState, extra) => {
    const { api } = extra;
    const {
      locale: { locale },
    } = getState();

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: true });

    const requestedBody = {
      activate: form.activate,
      is_direct_structure: form.isDirectStructureOnly || undefined,
      is_users_only: form.isPlayersOnly || undefined,
      is_agents_only: form.isAgentsOnly || undefined,
    };

    const response = await api.reports.activateCashbackForStructure(requestedBody);

    if (response.success) {
      dispatch({
        type: actionTypes.ACTIVATE_CASHBACK_FOR_STRUCTURE,
        payload: response.data,
      });
      dispatch(addNotify(locale.defaultSuccess, 'success'));
    } else {
      const errorMessage = getErrorMessage(locale, response);
      dispatch(addNotify(errorMessage, 'error'));
    }

    dispatch({ type: actionTypes.ACTION_PROCESSING, payload: false });
  };
}

export {
  actionTypes,
  getStatisticProvider,
  getPaymentHistory,
  getCashbackHistory,
  activateCashbackForOneUser,
  activateCashbackForStructure,
  getStatisticPlayer,
  getSlotsHistory,
  getBetsHistory,
  getPaymentHistoryTotals,
  getCBU,
  setCBU,
  getPlayerDeposits,
  changePlayerDeposit,
  createBank,
  deleteBank,
  updateBank,
  setDepositBonusPercent,
  getRequestHistory,
};
